import mqtt, { MqttClient } from 'mqtt';

import { store } from '../../store';

let client: MqttClient | null = null;
const origin = import.meta.env.VITE_MQTT_ORIGIN ?? '';
const brokerUrl = import.meta.env.VITE_BROKER_URL ?? '';
const mqttUsername = import.meta.env.VITE_MQTT_USERNAME ?? '';
const mqttPassword = import.meta.env.VITE_MQTT_PASSWORD ?? '';

const topicCallbacks: {
  [topic: string]: Set<(topic: string, message: Uint8Array) => void>;
} = {};

const TAB_KEY = 'mqttTabId';
const isReload =
  (
    performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined
  )?.type === 'reload';
const tabId = (() => {
  if (isReload) {
    const saved = sessionStorage.getItem(TAB_KEY);
    if (saved) return saved;
  }
  const id = Math.random().toString(36).slice(2, 8);
  sessionStorage.setItem(TAB_KEY, id);
  return id;
})();

function getClientId(): string {
  const userId = store.getState().profile?.profileData?.id;
  if (!userId) throw new Error('userId not available yet');
  return `${origin}-${userId}-${tabId}`;
}

export function getMqttClient(): MqttClient {
  if (client) return client;

  client = mqtt.connect(brokerUrl, {
    protocolVersion: 5 as const,
    clientId: getClientId(),
    username: mqttUsername,
    password: mqttPassword,
    clean: true,
  });

  client.on('message', (receivedTopic, message) => {
    const originalTopic = receivedTopic.replace(origin, '');
    Object.entries(topicCallbacks).forEach(([subTopic, callbacks]) => {
      const base = subTopic.replace('#', '');
      if (receivedTopic.startsWith(`${origin}${base}`)) {
        callbacks.forEach(cb => cb(originalTopic, message));
      }
    });
  });

  client.on('error', err => {
    console.error('MQTT error:', err);
  });

  return client;
}

export function initMqttClient(): void {
  if (client && !client.disconnected && !client.disconnecting) return;
  if (client) {
    client.end(true);
    client = null;
  }
  const userId = store.getState().profile?.profileData?.id;
  if (!userId) return;
  getMqttClient();
}

export function subscribe(
  topic: string,
  callback: (topic: string, message: Uint8Array) => void,
) {
  const fullTopic = `${origin}${topic}`;
  const mqttClient = getMqttClient();

  if (!topicCallbacks[topic]) {
    topicCallbacks[topic] = new Set();
    mqttClient.subscribe(fullTopic, err => {
      if (err) console.error(`Failed to subscribe to ${fullTopic}`, err);
    });
  }

  topicCallbacks[topic].add(callback);
}

export function unsubscribe(
  topic: string,
  callback: (topic: string, message: Uint8Array) => void,
) {
  if (!topicCallbacks[topic]) return;

  topicCallbacks[topic].delete(callback);

  if (topicCallbacks[topic].size === 0) {
    if (client) {
      client.unsubscribe(`${origin}${topic}`);
    }
    delete topicCallbacks[topic];
  }
}

export const publish = (
  topic: string,
  message: string,
  callback?: (err?: Error) => void,
) => {
  const fullTopic = `${origin}${topic}`;
  const client = getMqttClient();

  try {
    const parsed = JSON.parse(message);
    if (parsed?.provisional) {
      sessionStorage.setItem('pendingProvisionalId', parsed.provisional);
      sessionStorage.setItem(
        `createdAt:${parsed.provisional}`,
        new Date().toISOString(),
      );
      sessionStorage.setItem(`messageSent:${parsed.provisional}`, 'true');
    }
  } catch (err) {
    console.error(err);
  }

  const token = localStorage.getItem('token');

  const options: mqtt.IClientPublishOptions = {
    properties: {
      userProperties: {
        token: `Bearer ${token}`,
      },
    },
  };

  client.publish(fullTopic, message, options, callback);
};
